/**
 * Leviathan Web - Dashboard Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Command Center - Customizable Analytics Dashboard
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Settings, Save, X } from 'lucide-react';
import { API_URL } from '@/config/api';
import { useDashboardStore, LayoutItem, WidgetConfig } from '@/stores/dashboardStore';
import DashboardGrid from '@/components/dashboard/DashboardGrid';
import WidgetPicker from '@/components/dashboard/WidgetPicker';

/**
 * Dashboard page component
 */
const DashboardPage = () => {
  const { accessToken } = useAuth();
  const { isEditing, setEditing, setConfig, layout, widgets } = useDashboardStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch dashboard config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/dashboard-config`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setConfig(
            data.layout as LayoutItem[],
            data.widgets as WidgetConfig[]
          );
        }
      } catch (error) {
        console.error('Failed to fetch dashboard config:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [accessToken, setConfig]);

  // Save layout to API
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await fetch(`${API_URL}/api/settings/dashboard-config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ layout, widgets }),
      });
    } catch (error) {
      console.error('Failed to save dashboard config:', error);
    } finally {
      setIsSaving(false);
      setEditing(false);
    }
  }, [accessToken, layout, widgets, setEditing]);

  const handleCancelEdit = useCallback(() => {
    setEditing(false);
    // Re-fetch to discard changes
    const refetch = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/dashboard-config`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setConfig(
            data.layout as LayoutItem[],
            data.widgets as WidgetConfig[]
          );
        }
      } catch {
        // silent
      }
    };
    refetch();
  }, [accessToken, setEditing, setConfig]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-amber font-mono animate-pulse">Loading Command Center...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Scan line overlay */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-20">
        <div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-amber/5 to-transparent"
          style={{ animation: 'scan 10s linear infinite' }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Command Center
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-text-secondary text-sm font-mono">System Overview</span>
                <span className="text-xs text-text-tertiary">|</span>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-2 h-2 bg-success-green rounded-full animate-pulse"
                    style={{ boxShadow: '0 0 8px rgba(74,222,128,1)' }}
                  />
                  <span className="text-xs text-success-green font-mono uppercase tracking-wider">
                    All Systems Operational
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Customize / Save controls */}
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowWidgetPicker(true)}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-steel/10 hover:bg-steel/20 border border-steel/30 text-steel text-xs font-mono uppercase tracking-wider transition-colors rounded"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    <span>Widgets</span>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-error-red/10 hover:bg-error-red/20 border border-error-red/30 text-error-red text-xs font-mono uppercase tracking-wider transition-colors rounded"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-amber/10 hover:bg-amber/20 border border-amber/30 text-amber text-xs font-mono uppercase tracking-wider transition-colors rounded disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" />
                    <span>{isSaving ? 'Saving...' : 'Save Layout'}</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-panel-dark hover:bg-panel-border/40 border border-panel-border text-text-secondary hover:text-amber text-xs font-mono uppercase tracking-wider transition-colors rounded"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Customize</span>
                </button>
              )}

              <div className="text-right font-mono text-text-tertiary text-sm">
                <div className="text-amber">{currentTime.toLocaleDateString()}</div>
                <div>{currentTime.toLocaleTimeString()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit mode banner */}
      {isEditing && (
        <div className="relative z-10 bg-amber/5 border border-amber/20 rounded px-4 py-2 flex items-center space-x-3">
          <div className="w-2 h-2 bg-amber rounded-full animate-pulse" />
          <span className="text-xs font-mono text-amber uppercase tracking-wider">
            Edit Mode Active - Drag widgets to reposition, resize from corners
          </span>
        </div>
      )}

      {/* Widget grid */}
      <div className="relative z-10">
        <DashboardGrid />
      </div>

      {/* Widget picker modal */}
      {showWidgetPicker && (
        <WidgetPicker onClose={() => setShowWidgetPicker(false)} />
      )}
    </div>
  );
};

export default DashboardPage;
